"use client";

import Link from "next/link";
import { useState } from "react";
import { I } from "@/components/kamibot-v2/discord";

type SafetyKey = "shield" | "x" | "lock" | "heart";

const SAFETY: { ic: SafetyKey; title: string; body: string }[] = [
  {
    ic: "shield",
    title: "14일 환불 보장",
    body: "써보고 마음에 안 드시면 14일 안에 100% 환불해드려요. 이유는 안 물어볼게요. 정말로요.",
  },
  {
    ic: "x",
    title: "언제든 해지",
    body: "다음 결제일 전에 한 번이면 끝이에요. 해지 후에도 결제한 기간까지는 그대로 쓸 수 있어요.",
  },
  {
    ic: "lock",
    title: "무료 기능은 영원히 무료",
    body: "지금 무료로 쓰시는 기능을 나중에 유료로 바꾸지 않아요. 약속해요.",
  },
  {
    ic: "heart",
    title: "미안해할 필요 없어요",
    body: "멤버십에 가입하지 않으셔도 카미봇의 핵심 기능은 똑같이 잘 작동해요. 필요하실 때만 와주세요.",
  },
];

const FAQ_ITEMS: { tag: string; q: string; a: string[] }[] = [
  {
    tag: "PRICING",
    q: "한 번에 일년치 결제 같은 일회성 결제는 안 되나요?",
    a: [
      "연간 결제가 그 역할을 해요. 한 번 결제로 1년 동안 결제 알림 없이 쓸 수 있어요.",
      "더 자유로운 단발성 결제는 별도로 \"커피 사주기\" 페이지를 곰 열 예정이에요.",
    ],
  },
  {
    tag: "REFUND",
    q: "환불 절차가 복잡하지 않나요?",
    a: [
      "디스코드 카미봇 지원 서버에서 \"환불\"이라고 한마디만 주시면 돼요.",
      "이유도 묻지 않고, 14일 안이면 100%, 그 이후라도 사용량에 비례해서 환불해드려요.",
    ],
  },
  {
    tag: "TAX",
    q: "세금계산서나 영수증이 필요해요.",
    a: [
      "네, 결제 후 메일로 자동 발행돼요. 사업자등록번호로 발행이 필요하시면 결제 시 입력란이 있어요.",
      "기업 길드 멤버십이나 단체 결제는 따로 메일 주시면 견적 도와드려요.",
    ],
  },
  {
    tag: "GUILD",
    q: "길드/서버 단위로 가입할 수 있나요?",
    a: [
      "스튜디오 플랜이 길드 멤버십에 가까워요. 디스코드 서버 부스트처럼, 한 분이 결제하시면 그 서버 전체가 커스텀 봇 + 멤버십 혜택을 받아요.",
      "여러 명이 함께 부담하고 싶으시면 지원 서버에 알려주세요. 분할 결제 도와드려요.",
    ],
  },
  {
    tag: "USAGE",
    q: "멤버십 수익을 어디에 쓰는지 알 수 있나요?",
    a: [
      "네. 분기마다 사용 내역을 멤버 전용 채널에 공개해요 (인프라 ₩, AI API ₩, TTS ₩, 운영 ₩).",
      "장부를 다 까는 건 아니지만, 카미봇이 어떻게 살아 있는지는 솔직하게 보여드릴게요.",
    ],
  },
  {
    tag: "FREE",
    q: "멤버십 안 하면 미안한 분위기 아니가요?",
    a: [
      "전혀요. 무료 분들이 카미봇의 기본이에요. 서포터 멤버님 덕분에 무료 분들이 더 풍요롭게 쓰실 수 있는 구조예요.",
      "정말 마음 편하게 무료로 오래 써주세요.",
    ],
  },
];

const SAFETY_ICONS = {
  shield: I.shield,
  x: I.x,
  lock: I.lock,
  heart: I.heart,
} as const;

export function PremiumFAQ() {
  const [open, setOpen] = useState<number>(0);

  const handleFinalCta = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById("plans");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <section className="prem-safety">
        <div className="shell">
          <div className="section-head">
            <span className="section-eyebrow">
              <I.shield /> SAFETY · 안전장치
            </span>
            <h2 className="section-title">결제 전에 — 약속할게요.</h2>
            <p className="section-sub">
              구독은 한 번 들어오면 못 나오는 게 아니에요. 카미봇은 그렇게 안 만들어요.
            </p>
          </div>
          <div className="prem-safety-grid">
            {SAFETY.map((s, i) => {
              const Ic = SAFETY_ICONS[s.ic];
              return (
                <div key={i} className="prem-safety-card">
                  <span className="prem-safety-ic">
                    <Ic />
                  </span>
                  <b>{s.title}</b>
                  <p>{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="prem-faq" id="faq">
        <div className="shell">
          <div className="section-head">
            <span className="section-eyebrow">
              <I.help /> FAQ · 자주 묻는 질문
            </span>
            <h2 className="section-title">남은 궁금증, 정리해드릴게요.</h2>
          </div>
          <div className="faq-list">
            {FAQ_ITEMS.map((it, i) => (
              <div
                key={i}
                className="faq-item"
                data-open={open === i ? "true" : "false"}
              >
                <button
                  type="button"
                  className="faq-q"
                  onClick={() => setOpen(open === i ? -1 : i)}
                >
                  <span className="faq-tag">{it.tag}</span>
                  <span>{it.q}</span>
                  <I.chev className="chev" />
                </button>
                <div className="faq-a">
                  <div>
                    {it.a.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="prem-final">
        <div className="shell">
          <div className="prem-final-card">
            <span className="prem-final-mark">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/kamibot-character/kamibot-portrait.png" alt="" />
            </span>
            <h3 className="gowun">결정은 천천히 하셔도 돼요.</h3>
            <p className="gowun">
              지원 서버에서 직접 물어봐주셔도 좋고, 무료로 한 달 더 써보시고 결정하셔도 좋아요.
              저는 어느 쪽이든 좋아요.
            </p>
            <div className="prem-final-actions">
              <a className="btn btn-primary" href="#plans" onClick={handleFinalCta}>
                <I.heart /> 멤버십 가입하기
              </a>
              <Link className="btn btn-ghost" href="/">
                메인으로 돌아가기 <I.arrow />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
